import { v4 as uuid } from "uuid";
import moment from "moment";

function convertDateToUTC(timestamp) {
  const date = new Date(timestamp);

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  ).getTime();
}

export const generateXMLString = (data, activeCheckboxes, activeChildrens) => {
  return `<?xml version="1.0" encoding="utf-8"?>
    <rss version="2.0" xmlns:atom="http://www.w3.org/2005/Atom" xmlns:itunes="http://www.itunes.com/dtds/podcast-1.0.dtd" xmlns:itunesu="http://www.itunesu.com/feed">
        <channel>
            <title>${data.title}</title>
            <description>${data.summary}</description>
            <itunes:image href="${data.image_url}"/>
            <language>${data.lang}</language>
            <copyright>${data.copyright}</copyright>
            <link>${data.url}</link>
            <image>
                <url>${data.image_url}</url>
                <title>${data.title}</title>
                <link>${data.url}</link>
            </image>
            <itunes:summary>${data.summary}</itunes:summary>
            <itunes:subtitle>${data.subtitle}</itunes:subtitle>
            <itunes:owner>
                <itunes:name>${data.owner}</itunes:name>
                <itunes:email>${data.cemail}</itunes:email>
            </itunes:owner>
            <itunes:author>${data.author}</itunes:author>
            
            ${activeCheckboxes
              ?.map((box) => {
                const childrens = activeChildrens.filter(
                  (child) => child.parentId === box.id
                );

                if (childrens.length === 0) {
                  return `<itunes:category text="${box.value.replace(
                    "and",
                    "&amp;"
                  )}" />`;
                } else {
                  return `<itunes:category text="${box.value.replace(
                    "and",
                    "&amp;"
                  )}">
                  ${childrens
                    .map(
                      (child) =>
                        `<itunes:category text="${child.value.replace(
                          "and",
                          "&amp;"
                        )}" />`
                    )
                    .join("\n                  ")}
            </itunes:category>`;
                }
              })
              .join("\n            ")}

            <itunes:keywords>${data.keywords}</itunes:keywords>
            <itunes:explicit>${data.age_restriction}</itunes:explicit>

            ${data.episodes
              ?.map((ep, i) => {
                const dateTime = new Date(
                  `${ep.release_date} ${ep.release_time}`
                );
                const fullTime = dateTime.getTime();
                const utcDateTime = convertDateToUTC(fullTime);

                const utcDisplayDate = moment(utcDateTime).format(
                  "ddd, D MMM YYYY HH:mm"
                );

                let duration = `${ep.seconds}`;

                if (parseInt(ep.hours)) {
                  duration = `${ep.hours}:${ep.minutes}:${ep.seconds}`;
                } else if (parseInt(ep.minutes)) {
                  duration = `${ep.minutes}:${ep.seconds}`;
                }

                return `<item>
                <title>${ep.title}</title>
                <itunes:subtitle>${ep.subtitle}</itunes:subtitle>
                <description><![CDATA[${ep?.description?.replace(
                  /(\r\n|\n|\r)/gm,
                  ""
                )}]]></description>
                ${
                  data.hasContentEncoded
                    ? `<content:encoded><![CDATA[${ep?.description?.replace(
                        /(\r\n|\n|\r)/gm,
                        ""
                      )}]]></content:encoded>`
                    : ""
                }
                <enclosure type="audio/mpeg" url="${ep.file_url}" length="${
                  ep.video_length
                }"/>
                <guid>${ep.previusGuid || uuid()}</guid>
                <itunes:duration>${duration}</itunes:duration>
                <itunes:explicit>${ep.age_restriction}</itunes:explicit>
                <pubDate>${utcDisplayDate} UTC</pubDate>
            </item>`;
              })
              .join("\n            ")}
        </channel>
        <!-- Generated by Podcast XML Maker -->
    </rss>`.replaceAll("& ", "&amp; ");
};

export const extractXMLDoc = (xmlDoc) => {
  const channel = xmlDoc.getElementsByTagName("channel")[0];
  const returnObject = {};
  let hasContentEncoded = false;

  if (!channel) throw new Error("Invalid XML structure");

  const channelChildrens = channel.childNodes;
  if (!channelChildrens) throw new Error("Invalid XML structure");

  const loopAbleChannelChildrens = Array.prototype.slice.call(channelChildrens);

  // Title
  const title = loopAbleChannelChildrens.find(
    (child) => child.tagName === "title"
  );
  if (title) {
    returnObject.title = title?.textContent || "";
  }

  // Description and Summary
  const description = loopAbleChannelChildrens.find(
    (child) => child.tagName === "description"
  );

  if (description) {
    returnObject.summary = description?.textContent || "";
  }

  // Image Url
  const image_url = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:image"
  );
  if (image_url) {
    const urlValue = image_url?.getAttribute("href");
    returnObject.image_url = urlValue || "";
  }

  // Subtitle
  const subtitle = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:subtitle"
  );
  if (subtitle) {
    returnObject.subtitle = subtitle?.textContent || "";
  }

  // Author
  const author = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:author"
  );
  if (author) {
    returnObject.author = author?.textContent || "";
  }

  // Owner and Email
  const ownerAndEmail = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:owner"
  );
  if (ownerAndEmail) {
    const loopAbleOwnerAndEmail = Array.prototype.slice.call(
      ownerAndEmail.childNodes
    );

    const owner = loopAbleOwnerAndEmail.find(
      (child) => child.tagName === "itunes:name"
    );

    if (owner) {
      returnObject.owner = owner?.textContent || "";
    }

    const email = loopAbleOwnerAndEmail.find(
      (child) => child.tagName === "itunes:email"
    );
    if (email) {
      returnObject.cemail = email?.textContent || "";
    }
  }

  // URL
  const url = loopAbleChannelChildrens.find(
    (child) => child.tagName === "link"
  );
  if (url) {
    returnObject.url = url?.textContent || "";
  }

  // Lang
  const lang = loopAbleChannelChildrens.find(
    (child) => child.tagName === "language"
  );
  if (lang) {
    returnObject.lang = lang?.textContent || "";
  }

  // CopyRight
  const copyright = loopAbleChannelChildrens.find(
    (child) => child.tagName === "copyright"
  );
  if (copyright) {
    returnObject.copyright = copyright?.textContent || "";
  }

  // Keywords
  const keywords = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:keywords"
  );
  if (keywords) {
    returnObject.keywords = keywords?.textContent || "";
  }

  // Age restriction
  const age_restriction = loopAbleChannelChildrens.find(
    (child) => child.tagName === "itunes:explicit"
  );
  if (age_restriction) {
    const value = age_restriction?.textContent;
    if (value === "clean" || value === "no" || value === "false") {
      returnObject.age_restriction = "false";
    }
    if (value === "explicit" || value === "yes" || value === "true") {
      returnObject.age_restriction = "true";
    }
  }

  // Categories
  const categories = loopAbleChannelChildrens.filter(
    (child) => child.nodeName === "itunes:category"
  );

  const activeCheckboxes = [];
  const activeChildrens = [];

  categories.forEach((cat) => {
    activeCheckboxes.push({
      id: cat.attributes.text.textContent,
      value: cat.attributes.text.textContent,
    });
    // Fetching childrens
    let loopAbleCategoryChilds = Array.prototype.slice.call(cat.childNodes);

    loopAbleCategoryChilds = loopAbleCategoryChilds.filter(
      (child) => child.nodeName === "itunes:category"
    );

    loopAbleCategoryChilds.map((subCat) => {
      activeChildrens.push({
        parentId: cat.attributes.text.textContent,
        id: subCat.attributes.text.textContent,
        value: subCat.attributes.text.textContent,
      });
    });
  });

  returnObject.categories = {
    activeCheckboxes,
    activeChildrens,
  };

  const episodes = loopAbleChannelChildrens.filter(
    (child) => child.tagName === "item"
  );

  if (episodes[0]) {
    const allEpisodes = [];
    episodes.forEach((epiElm) => {
      const returnEpisode = {};

      const episodesChildren = epiElm.childNodes;
      const loopAbleEpisodes = Array.prototype.slice.call(episodesChildren);

      // Episode Title
      const title = loopAbleEpisodes.find((child) => child.tagName === "title");
      if (title) {
        returnEpisode.title = title?.textContent || "";
      }

      // Episode Subtitle
      const subtitle = loopAbleEpisodes.find(
        (child) => child.tagName === "itunes:subtitle"
      );
      if (subtitle) {
        returnEpisode.subtitle = subtitle?.textContent || "";
      }

      // Episode Description
      const description = loopAbleEpisodes.find(
        (child) => child.tagName === "description"
      );
      const encodedContent = loopAbleEpisodes.find(
        (child) => child.tagName === "content:encoded"
      );

      if (description) {
        returnEpisode.description = description?.textContent?.trim() || "";
      }
      if (encodedContent) {
        returnEpisode.description = encodedContent?.textContent?.trim() || "";
        hasContentEncoded = true;
      }

      // Episode URL and Length
      const episodeAndLength = loopAbleEpisodes.find(
        (child) => child.tagName === "enclosure"
      );
      if (episodeAndLength) {
        const file_url = episodeAndLength.getAttribute("url");
        const video_length = episodeAndLength.getAttribute("length");

        if (file_url) {
          returnEpisode.file_url = file_url;
        }
        if (video_length) {
          returnEpisode.video_length = video_length;
        }
      }

      // Duration
      const duration = loopAbleEpisodes.find(
        (child) => child.tagName === "itunes:duration"
      );
      if (duration) {
        const durationArray = (duration?.textContent || "").split(":");

        let hours = "0",
          minutes = "0",
          seconds = "0";
        if (durationArray.length === 1) {
          seconds = durationArray[0];
        }
        if (durationArray.length === 2) {
          minutes = durationArray[0];
          seconds = durationArray[1];
        }
        if (durationArray.length === 3) {
          hours = durationArray[0];
          minutes = durationArray[1];
          seconds = durationArray[2];
        }

        if (hours) {
          returnEpisode.hours = hours;
        }
        if (minutes) {
          returnEpisode.minutes = minutes;
        }
        if (seconds) {
          returnEpisode.seconds = seconds;
        }
      }

      // Age restriction
      const age_restriction = loopAbleEpisodes.find(
        (child) => child.tagName === "itunes:explicit"
      );
      if (age_restriction) {
        const value = age_restriction?.textContent;
        if (value === "clean" || value === "no" || value === "false") {
          returnEpisode.age_restriction = "false";
        }
        if (value === "explicit" || value === "yes" || value === "true") {
          returnEpisode.age_restriction = "true";
        }
      }

      // GUID
      const guid = loopAbleEpisodes.find((child) => child.tagName === "guid");
      if (guid) {
        returnEpisode.previusGuid = guid?.textContent || null;
      }

      // Release Date and Time
      const date_and_time = loopAbleEpisodes.find(
        (child) => child.tagName === "pubDate"
      );
      if (date_and_time) {
        const theDate = moment(date_and_time?.textContent || "");
        const currnetDate = theDate.format("YYYY-MM-DD");
        const currentTime = theDate.format("HH:mm");

        if (currnetDate) {
          returnEpisode.release_date = currnetDate;
        }
        if (currentTime) {
          returnEpisode.release_time = currentTime;
        }
      }

      allEpisodes.push(returnEpisode);
    });

    returnObject.episodes = allEpisodes;
  }

  returnObject.hasContentEncoded = hasContentEncoded;

  return returnObject;
};
